// Shared visual language for every non-gameboard screen: surfaces, accents,
// type and motion. Components should pull from here instead of hard-coding
// one-off gradients so menus, modals and screens read as one product.

export const fonts = {
  display: `"Lilita One", "Poppins", sans-serif`,
  body: `"Poppins", sans-serif`,
};

export const colors = {
  void: "#07030f",
  voidDeep: "#040109",
  ink: "#0d0720",
  surface: "rgba(18, 10, 40, 0.78)",
  surfaceSolid: "#140a2c",
  surfaceRaised: "rgba(32, 18, 68, 0.86)",
  surfaceHover: "rgba(52, 32, 104, 0.9)",
  line: "rgba(255, 255, 255, 0.08)",
  lineStrong: "rgba(255, 255, 255, 0.16)",
  violet: "#7c5cff",
  violetBright: "#9d85ff",
  violetDeep: "#3b1f9e",
  cyan: "#35d6ff",
  magenta: "#ff3d9a",
  gold: "#ffd36a",
  goldBright: "#ffe9a8",
  goldDeep: "#c98a1b",
  coral: "#ff4d6d",
  success: "#3ee08f",
  danger: "#ff4d6d",
  text: "#ffffff",
  textSoft: "rgba(236, 230, 255, 0.78)",
  textMuted: "rgba(214, 204, 255, 0.52)",
};

export const gradients = {
  panel:
    "linear-gradient(180deg, rgba(34, 20, 74, 0.92) 0%, rgba(14, 7, 32, 0.94) 100%)",
  panelSheen:
    "radial-gradient(120% 70% at 50% -10%, rgba(140, 108, 255, 0.28) 0%, rgba(140, 108, 255, 0) 60%)",
  gold: "linear-gradient(180deg, #ffe9a8 0%, #ffd36a 45%, #e9a334 100%)",
  goldLine:
    "linear-gradient(90deg, rgba(255, 211, 106, 0) 0%, rgba(255, 211, 106, 0.85) 50%, rgba(255, 211, 106, 0) 100%)",
  violet: "linear-gradient(180deg, #9d85ff 0%, #6a46ff 55%, #4a28d8 100%)",
  play: "linear-gradient(180deg, #ff7a8f 0%, #ff4d6d 45%, #d81f4a 100%)",
  cyan: "linear-gradient(180deg, #7de6ff 0%, #2fb8f0 55%, #1673c9 100%)",
  screenVignette:
    "radial-gradient(120% 90% at 50% 40%, rgba(7, 3, 15, 0) 40%, rgba(7, 3, 15, 0.75) 100%)",
};

export const radii = {
  sm: 8,
  md: 12,
  lg: 16,
  xl: 22,
  pill: 999,
};

export const shadows = {
  panel:
    "0 24px 60px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 255, 255, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.08)",
  raised:
    "0 10px 24px rgba(0, 0, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.12)",
  glowViolet: "0 0 24px rgba(124, 92, 255, 0.45)",
  glowGold: "0 0 22px rgba(255, 211, 106, 0.45)",
  glowPlay: "0 10px 30px rgba(255, 77, 109, 0.45)",
};

export const blur = {
  glass: "blur(14px) saturate(140%)",
};

// Ready-made sx fragments.
export const glassPanelSx = {
  background: `${gradients.panelSheen}, ${gradients.panel}`,
  border: `1px solid ${colors.lineStrong}`,
  borderRadius: `${radii.lg}px`,
  boxShadow: shadows.panel,
  backdropFilter: blur.glass,
  WebkitBackdropFilter: blur.glass,
  color: colors.text,
};

export const glassChipSx = {
  background: "rgba(12, 6, 28, 0.62)",
  border: `1px solid ${colors.lineStrong}`,
  boxShadow: shadows.raised,
  backdropFilter: blur.glass,
  WebkitBackdropFilter: blur.glass,
  color: colors.text,
};

export const displayTextSx = {
  fontFamily: fonts.display,
  fontWeight: 400,
  letterSpacing: "0.02em",
  color: colors.text,
  textShadow: "0 2px 0 rgba(0, 0, 0, 0.35), 0 6px 18px rgba(0, 0, 0, 0.45)",
};

export const eyebrowTextSx = {
  fontFamily: fonts.body,
  fontWeight: 700,
  fontSize: "0.68rem",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  color: colors.textMuted,
};

export const focusRingSx = {
  "&:focus-visible": {
    outline: `2px solid ${colors.gold}`,
    outlineOffset: "3px",
  },
};

export const RARITY_COLORS = {
  Common: "#b8c0d6",
  Uncommon: "#3ee08f",
  Rare: "#35a8ff",
  Epic: "#b56bff",
  Legendary: "#ffb23e",
};

// MUI Dialog chrome shared by the menu dialogs (Mail, Level, Wallet...).
export const dialogPaperSx = {
  borderRadius: "20px",
  overflow: "hidden",
  color: colors.text,
  backgroundColor: colors.surfaceSolid,
  backgroundImage: `${gradients.panelSheen}, ${gradients.panel}`,
  border: `1px solid ${colors.lineStrong}`,
  boxShadow: `${shadows.panel}, 0 0 60px rgba(124, 92, 255, 0.28)`,
};

export const dialogTitleSx = {
  position: "relative",
  px: { xs: 2, md: 3 },
  py: 1.75,
  background: "linear-gradient(180deg, rgba(124, 92, 255, 0.16) 0%, rgba(124, 92, 255, 0) 100%)",
  borderBottom: `1px solid ${colors.line}`,
  "&::after": {
    content: '""',
    position: "absolute",
    left: "6%",
    right: "6%",
    bottom: -1,
    height: "1px",
    background: gradients.goldLine,
    opacity: 0.8,
  },
};

export const dialogTitleTextSx = {
  fontFamily: fonts.display,
  fontWeight: 400,
  fontSize: { xs: "1.45rem", md: "1.7rem" },
  lineHeight: 1.1,
  letterSpacing: "0.02em",
  color: colors.text,
  textShadow: "0 2px 12px rgba(0, 0, 0, 0.5)",
  flexGrow: 1,
};

export const dialogCloseButtonSx = {
  width: 38,
  height: 38,
  color: colors.text,
  borderRadius: `${radii.md}px`,
  border: `1px solid ${colors.lineStrong}`,
  backgroundColor: "rgba(255, 255, 255, 0.06)",
  transition: "background-color 150ms ease, transform 150ms ease",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.14)", transform: "rotate(90deg)" },
};

// Paper surface without shape, for dialogs that set their own radius
// (full-screen on mobile, etc.).
export const dialogSurfaceSx = {
  color: colors.text,
  backgroundColor: colors.surfaceSolid,
  backgroundImage: `${gradients.panelSheen}, ${gradients.panel}`,
  border: `1px solid ${colors.lineStrong}`,
  boxShadow: `${shadows.panel}, 0 0 60px rgba(124, 92, 255, 0.28)`,
};
